import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import {isFirstLoad} from 'nutshell-core/utils/graphql-loading';

import type {FormSortType} from 'nutshell-graphql-types';
import type {
    GetForms as GetFormsQuery,
    GetFormsVariables as GetFormsQueryVariables,
    FormStubFragment as FormStub,
} from 'nutshell-graphql-types';

import {getFormListQueryVariables} from '../../graphql/graphql-wrappers/helpers';
import GET_FORMS from '../../graphql/queries/get-forms.graphql';

type Props = {
    isArchived?: boolean;
    sortOrder: FormSortType;
    searchQuery?: string;
    limit?: number;
    creatorUserId?: string;
    lastUpdatedTime?: string | null | undefined;
    children: (arg1: {
        forms: FormStub[];
        totalCount: number;
        error: string | null | undefined;
        isLoading: boolean;
        fetchMoreRows: () => Promise<any>;
        hasNextPage: boolean;
        isFirstLoad: boolean;
    }) => React.ReactElement;
};

export function FormListQuery(props: Props) {
    const variables = getFormListQueryVariables(
        props.isArchived || false,
        props.sortOrder,
        props.searchQuery,
        props.limit,
        props.creatorUserId,
        props.lastUpdatedTime
    );

    const {
        data,
        loading: isLoading,
        error,
        fetchMore,
        networkStatus,
    } = useQuery<GetFormsQuery, GetFormsQueryVariables>(GET_FORMS, {
        variables: variables,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const formsData = data ? data.forms : null;

    const forms = formsData
        ? formsData.edges.map((campaign) => {
              return campaign.node;
          })
        : [];

    const totalCount = formsData ? formsData.totalCount : 0;

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: props.limit,
                after: formsData ? formsData.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetFormsQuery,
                newResults: {
                    fetchMoreResult: GetFormsQuery;
                }
            ) => {
                const oldForms = prev.forms;
                const fetchedForms = newResults.fetchMoreResult.forms;

                const newForms = {
                    ...oldForms,
                    pageInfo: fetchedForms.pageInfo,
                    edges: oldForms.edges.concat(fetchedForms.edges),
                } as const;

                return {forms: newForms};
            },
        });
    };

    const hasNextPage = formsData ? formsData.pageInfo.hasNextPage : false;

    return props.children({
        forms,
        totalCount,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        fetchMoreRows,
        hasNextPage,
        isFirstLoad: isFirstLoad(networkStatus),
    });
}
